import { FC, useEffect, useRef, CSSProperties, useMemo } from 'react';

import { debounce } from 'common/utils';
import {
  init,
  getInstanceByDom,
  EChartsOption,
  EChartsInitOpts,
} from 'echarts';

interface IEChartsProps {
  option: EChartsOption;
  chartSettings?: EChartsInitOpts;
  style?: CSSProperties;
}

export const ECharts: FC<IEChartsProps> = ({
  option,
  chartSettings,
  style = { width: '100%', height: '350px' },
  ...props
}) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const chart = init(chartRef.current, null, chartSettings);

    return () => {
      chart?.dispose();
    };
  }, []);

  useEffect(() => {
    if (chartRef.current === null) {
      return;
    }

    const chart = getInstanceByDom(chartRef.current);

    if (chart !== undefined) {
      chart.setOption(option);
    }
  }, [option]);

  const resizeChart = useMemo(
    () =>
      debounce(() => {
        if (chartRef.current === null) {
          return;
        }

        const chart = getInstanceByDom(chartRef.current);

        if (chart !== undefined) {
          chart.resize();
        }
      }, 50),
    [],
  );

  const resizeObserver = new ResizeObserver(() => {
    resizeChart();
  });

  if (chartRef.current !== null) {
    resizeObserver.observe(chartRef.current);
  }

  return <div ref={chartRef} style={style} {...props} />;
};
